import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiResult } from '../application-api/application-api.types';
import {
  CompareAndFraudApplicant,
  CompareResponse,
  ContactInformation,
  FraudResponse,
  Identification,
  InstaTouchSession,
  InstaTouchUserInformation,
  Name,
  InstatouchAutofillApplicants,
  OAOCompareAndFraudApplicant,
  InstaTouchAddress,
  OAOInstatouchVerification,
  GetPersonalInformationParams
} from '../instaTouch/insta-touch.types';
import { ApiService } from '@workflow/ecp-shared/service/api/api.service';
import { OAOApplicantType, OAOSourceApplicantType } from '@workflow/ecp-shared/enum/enum';
import { CrudApiBackend } from '@workflow/class/crud-api-backend';
import { StorageService } from '@workflow/service/storage/storage.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { LoanType } from '@shared/enum';
import { StatefulService } from '@app/service/stateful.service';
import { RequestHeaderConst } from '@workflow/ecp-shared/constant/information.const';

/**
 * Request methods for application related calls
 */
@Injectable({
  providedIn: 'root'
})
export class InstaTouchService {
  /**
   * HostName
   */
  private readonly hostName = 'consumerPortalApi';

  /**
   * Api Root URL.
   */
  protected readonly apiRoot = this.apiService.GetFullURL(this.hostName, '');

  /**
   * API backend for mortgage
   */
  // eslint-disable-next-line prettier/prettier
  protected get crudApiService() {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    return this.storageService.backends.api as CrudApiBackend;
  }

  constructor(
    protected readonly httpClient: HttpClient,
    protected readonly apiService: ApiService,
    protected readonly storageService: StorageService,
    public readonly userService: UserService,
    private readonly statefulService: StatefulService
  ) {}

  protected httpOptions(): { headers: HttpHeaders } {
    let httpHeaders = this.crudApiService?.headers && this.crudApiService.headers();
    if (!(httpHeaders instanceof HttpHeaders)) {
      httpHeaders = new HttpHeaders();
    }
    const authToken = this.getAuthTokenBasedOnFlowType;
    // Set flow based token for dxSessionId and Authorization headers
    httpHeaders = httpHeaders.set(RequestHeaderConst.dxSessionId, authToken).set(RequestHeaderConst.authorization, authToken);
    return { headers: httpHeaders };
  }

  /**
   * Method to get the correct Auth Token based on flow type: OLA or OAO
   */
  protected get getAuthTokenBasedOnFlowType(): string {
    const olaToken = this.statefulService.wrap('ss:auth-ola').valueString;
    const oaoToken = this.statefulService.wrap('ss:authorization').valueString;
    return this.userService?.flowType?.toLowerCase() === LoanType.OLA.toLowerCase() ? olaToken : oaoToken;
  }

  public generatePasscode(mobileNumber: string): Observable<ApiResult<InstaTouchSession>> {
    const url = this.apiRoot + 'member/oao/integration/GeneratePasscode';
    return this.httpClient.post<ApiResult<InstaTouchSession>>(url, mobileNumber, this.httpOptions());
  }

  public validatePasscode(sessionId: string, passcode: string, requestId: string): Observable<ApiResult<boolean>> {
    const requestBody = {
      payload: { passcode },
      sessionId,
      requestId
    };

    const url = this.apiRoot + 'member/oao/integration/ValidatePasscode';
    return this.httpClient.post<ApiResult<boolean>>(url, requestBody, this.httpOptions());
  }

  public getPersonalInformation({
    sessionId,
    zipCode,
    last4ssn,
    requestId,
    isPrimaryBorrower = false,
    mobileNumber
  }: GetPersonalInformationParams): Observable<ApiResult<InstaTouchUserInformation>> {
    const requestBody = {
      payload: { zipCode, ssn: last4ssn, isPrimaryBorrower, mobileNumber },
      sessionId,
      requestId
    };

    const url = this.apiRoot + 'member/oao/integration/PersonalInformation';
    return this.httpClient.post<ApiResult<InstaTouchUserInformation>>(url, requestBody, this.httpOptions());
  }

  public getCompareResponse(applicant: OAOCompareAndFraudApplicant): Observable<ApiResult<CompareResponse>> {
    const url = this.apiRoot + 'member/oao/integration/CompareIdentity';
    return this.httpClient.post<ApiResult<CompareResponse>>(url, applicant, this.httpOptions());
  }

  public getFraudResponse(applicant: OAOCompareAndFraudApplicant): Observable<ApiResult<FraudResponse>> {
    const url = this.apiRoot + 'member/oao/integration/FraudVerification';
    return this.httpClient.post<ApiResult<FraudResponse>>(url, applicant, this.httpOptions());
  }

  public getCompareandFraudResponse(applicant: OAOCompareAndFraudApplicant[]): Observable<ApiResult<OAOInstatouchVerification[]>> {
    const url = this.apiRoot + 'member/oao/integration/comparefraudverification';
    return this.httpClient.post<ApiResult<OAOInstatouchVerification[]>>(url, applicant, this.httpOptions());
  }

  public getOAOComapreAndFraudApplicant(apiPrimaryRoot: string): CompareAndFraudApplicant {
    const names: Name = {
      firstName: this.statefulService.wrap(`${apiPrimaryRoot}/firstName`).valueString,
      lastName: this.statefulService.wrap(`${apiPrimaryRoot}/lastName`).valueString
    };
    const zipCode = this.statefulService.wrap(`${apiPrimaryRoot}/primaryAddress/$/zipCode`, 0).valueString;
    const fiveDigitZipCode = zipCode && zipCode.length > 5 ? zipCode.substring(0, 5) : zipCode;

    const currentAddress: InstaTouchAddress = {
      city: this.statefulService.wrap(`${apiPrimaryRoot}/primaryAddress/$/city`, 0).valueString,
      state: this.statefulService.wrap(`${apiPrimaryRoot}/primaryAddress/$/state`, 0).valueString,
      streetAddress: this.statefulService.wrap(`${apiPrimaryRoot}/primaryAddress/$/addressLine1`, 0).valueString,
      type: 'Current',
      zipCode: fiveDigitZipCode
    };

    const addresses: InstaTouchAddress[] = [currentAddress];
    const homePhone = this.statefulService.wrap(`${apiPrimaryRoot}/homePhone`).valueString;
    const mobileNumber = this.statefulService.wrap(`${apiPrimaryRoot}/mobileNumber`).valueString;
    const phoneNumber = homePhone ? homePhone : mobileNumber;
    const formattedPhoneNumber = phoneNumber ? phoneNumber.replace(/\D/g, '') : phoneNumber;
    const contacts: ContactInformation = {
      homePhone: formattedPhoneNumber
    };

    const formattedSsn = this.statefulService.wrap(`${apiPrimaryRoot}/ssn`).valueString;
    const identifications: Identification = {
      dob: this.statefulService.wrap(`${apiPrimaryRoot}/dateOfBirth`).valueString,
      ssn: formattedSsn ? formattedSsn.replace(/-/g, '') : formattedSsn
    };

    const applicant: CompareAndFraudApplicant = {
      addresses,
      contact: contacts,
      identification: identifications,
      name: names
    };
    return applicant;
  }
  // call Comapre And Fraud API if autofill option choosed, for manual no need
  public makeOAOComapreAndFraudAPICall(): InstatouchAutofillApplicants {
    const applicantList: InstatouchAutofillApplicants = {
      autofillPicked: false,
      applicants: []
    };

    // 1. identify Membership Type 1. Personal = 1, 2. Youth = 2, 3. Business = 3,
    const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;
    // Why is this assignemnt here?
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let apiPrimaryRoot = 'api:oaoMemberInformation/primaryApplicant';
    if (membershipTypeName && membershipTypeName.toLowerCase() === 'business') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      apiPrimaryRoot = 'api:oaoMemberInformation/businessInformation/responsibleParties/0';
    }

    switch (membershipTypeName && membershipTypeName.toLowerCase()) {
      case 'personal':
        const primaryApplicantOption = this.statefulService.wrap(
          'api:oaoMemberInformation/primaryApplicant/autoFillOption'
        ).valueStringOnly;
        if (primaryApplicantOption && primaryApplicantOption.toLowerCase() === 'mobilenumber') {
          applicantList.autofillPicked = true;
          const objApplicant = this.getOAOComapreAndFraudApplicant('api:oaoMemberInformation/primaryApplicant');
          applicantList.applicants.push({
            applicantId: this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/applicantId').valueNumberOnly,
            applicantTypeID: OAOApplicantType.Primary,
            sourceApplicantType: OAOSourceApplicantType.Personal,
            userIdentity: objApplicant
          } as OAOCompareAndFraudApplicant);
        }
        const coApplicantOption = this.statefulService.wrap('api:oaoMemberInformation/coApplicant/autoFillOption').valueStringOnly;
        if (coApplicantOption && coApplicantOption.toLowerCase() === 'mobilenumber') {
          applicantList.autofillPicked = true;
          const objApplicant = this.getOAOComapreAndFraudApplicant('api:oaoMemberInformation/coApplicant');
          applicantList.applicants.push({
            applicantId: this.statefulService.wrap('api:oaoMemberInformation/coApplicant/applicantId').valueNumberOnly,
            applicantTypeID: OAOApplicantType.CoApplicant,
            sourceApplicantType: OAOSourceApplicantType.Personal,
            userIdentity: objApplicant
          } as OAOCompareAndFraudApplicant);
        }
        const additionalCoApplicantOption = this.statefulService.wrap(
          'api:oaoMemberInformation/additionalCoApplicant/autoFillOption'
        ).valueStringOnly;
        if (additionalCoApplicantOption && additionalCoApplicantOption.toLowerCase() === 'mobilenumber') {
          applicantList.autofillPicked = true;
          const objApplicant = this.getOAOComapreAndFraudApplicant('api:oaoMemberInformation/additionalCoApplicant');
          applicantList.applicants.push({
            applicantId: this.statefulService.wrap('api:oaoMemberInformation/additionalCoApplicant/applicantId').valueNumberOnly,
            applicantTypeID: OAOApplicantType.Borrower3,
            sourceApplicantType: OAOSourceApplicantType.Personal,
            userIdentity: objApplicant
          } as OAOCompareAndFraudApplicant);
        }
        break;
      case 'business':
        const responsiblePartyCount = this.statefulService.wrap(
          'api:oaoMemberInformation/businessInformation/responsiblePartyCount'
        ).valueStringOnly;
        if (Number.parseInt(responsiblePartyCount) > 0) {
          const responsibleParties = this.statefulService.arraySize('api:oaoMemberInformation/businessInformation/responsibleParties');
          for (let i = 0; i < responsibleParties; i++) {
            const responsiblePartiesOption = this.statefulService.wrap(
              'api:oaoMemberInformation/businessInformation/responsibleParties/$/autoFillOption',
              i
            ).valueStringOnly;
            if (responsiblePartiesOption && responsiblePartiesOption.toLowerCase() === 'mobilenumber') {
              applicantList.autofillPicked = true;
              const objApplicant = this.getOAOComapreAndFraudApplicant(
                `api:oaoMemberInformation/businessInformation/responsibleParties/${i}`
              );
              applicantList.applicants.push({
                applicantId: this.statefulService.wrap(`api:oaoMemberInformation/businessInformation/responsibleParties/${i}/applicantId`)
                  .valueNumberOnly,
                applicantTypeID: i + 1,
                sourceApplicantType: OAOSourceApplicantType.ResponsibleParty,
                userIdentity: objApplicant
              } as OAOCompareAndFraudApplicant);
            }
          }
        }

        const additionalBeneficialOwnerCount = this.statefulService.wrap(
          'api:oaoMemberInformation/businessInformation/additionalBeneficialOwnerCount'
        ).valueStringOnly;
        if (Number.parseInt(additionalBeneficialOwnerCount) > 0) {
          const responsibleParties = this.statefulService.arraySize(
            'api:oaoMemberInformation/businessInformation/additionalBeneficialOwner'
          );
          for (let i = 0; i < responsibleParties; i++) {
            const responsiblePartiesOption = this.statefulService.wrap(
              'api:oaoMemberInformation/businessInformation/additionalBeneficialOwner/$/autoFillOption',
              i
            ).valueStringOnly;
            if (responsiblePartiesOption && responsiblePartiesOption.toLowerCase() === 'mobilenumber') {
              applicantList.autofillPicked = true;
              const objApplicant = this.getOAOComapreAndFraudApplicant(
                `api:oaoMemberInformation/businessInformation/additionalBeneficialOwner/${i}`
              );
              applicantList.applicants.push({
                applicantId: null,
                applicantTypeID: i + 1,
                sourceApplicantType: OAOSourceApplicantType.BeneficialOwner,
                userIdentity: objApplicant
              } as OAOCompareAndFraudApplicant);
            }
          }
        }

        const additionalAuthorizedCount = this.statefulService.wrap(
          'api:oaoMemberInformation/businessInformation/additionalAuthorizedCount'
        ).valueStringOnly;
        if (Number.parseInt(additionalAuthorizedCount) > 0) {
          const responsibleParties = this.statefulService.arraySize(
            'api:oaoMemberInformation/businessInformation/additionalAuthorizedSigners'
          );
          for (let i = 0; i < responsibleParties; i++) {
            const responsiblePartiesOption = this.statefulService.wrap(
              'api:oaoMemberInformation/businessInformation/additionalAuthorizedSigners/$/autoFillOption',
              i
            ).valueStringOnly;
            if (responsiblePartiesOption && responsiblePartiesOption.toLowerCase() === 'mobilenumber') {
              applicantList.autofillPicked = true;
              const objApplicant = this.getOAOComapreAndFraudApplicant(
                `api:oaoMemberInformation/businessInformation/additionalAuthorizedSigners/${i}`
              );
              applicantList.applicants.push({
                applicantId: null,
                applicantTypeID: i + 1,
                sourceApplicantType: OAOSourceApplicantType.AuthorizedSigners,
                userIdentity: objApplicant
              } as OAOCompareAndFraudApplicant);
            }
          }
        }

        break;

      default:
        break;
    }

    return applicantList;
  }
}
